import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useEffect, useRef, useState } from "react";

import { consumePendingFocus, openChat, useChatOpen } from "../../lib/chat";

/**
 * The assistant, sliding in from the edge.
 *
 * It enters horizontally from off-screen right rather than fading up from the
 * corner, which is what makes it read as *arriving* rather than as having
 * always been there under a spinner. It waits first: a support widget appears
 * immediately and says "How can we help?", which carries no information. This
 * waits until the headline has been read, then makes a specific claim.
 *
 * Structure, which is the accessibility of the thing:
 *
 *   The **robot is a button** with a real name — it is the control.
 *   The **message is a separate region**, `aria-live="polite"`, with its own
 *   dismiss. It gets announced without stealing focus, and it can be got rid
 *   of without dismissing the assistant itself.
 *
 * Collapsing those into one control is the usual mistake: the accessible name
 * becomes the whole sentence, and a screen reader reads a paragraph before
 * saying "button".
 *
 * Five idle motions, all on different periods. Anything looping in lockstep
 * reads as a loading spinner; offsetting them is most of what makes a drawing
 * feel alive rather than animated.
 */

/** Long enough to read the headline first. Short enough to still feel offered. */
const APPEAR_AFTER_MS = 2600;
const MESSAGE_AFTER_MS = 4300;

const MESSAGE = "Hello! I know everything here about Iftikhar — talk to me.";

function RobotFace({ animate }: { animate: boolean }) {
  return (
    <span
      aria-hidden="true"
      className="relative flex size-full items-center justify-center"
    >
      {/* Two blooms at different radii and periods. One glow reads as a
          drop shadow; two breathing out of phase reads as something powered. */}
      <motion.span
        className="absolute -inset-3 rounded-[1.6rem] bg-glow/45 blur-xl"
        animate={
          animate
            ? { scale: [1, 1.22, 1], opacity: [0.5, 0.9, 0.5] }
            : undefined
        }
        transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.span
        className="absolute -inset-1 rounded-[1.4rem] bg-accent/30 blur-md"
        animate={
          animate
            ? { scale: [1, 1.12, 1], opacity: [0.4, 0.75, 0.4] }
            : undefined
        }
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* The shell. A real border and a solid surface, so the face reads
          crisply against whatever is behind it. */}
      <span className="absolute inset-0 rounded-2xl border-2 border-accent-dim bg-surface shadow-[0_10px_30px_-10px_var(--color-accent)]" />

      {/* Orbiting satellite — one, not three. A ring of dots is a spinner. */}
      {animate && (
        <motion.span
          className="absolute -inset-2"
          animate={{ rotate: 360 }}
          transition={{ duration: 11, repeat: Infinity, ease: "linear" }}
        >
          <span className="absolute top-0 left-1/2 size-1.5 -translate-x-1/2 rounded-full bg-glow shadow-[0_0_8px_var(--color-glow)]" />
        </motion.span>
      )}

      <svg viewBox="0 0 40 40" className="relative size-full p-2 text-accent">
        <g
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
        >
          {/* Antenna, with a bulb that pulses off-beat from both blooms. */}
          <path d="M20 8V4.5" />
          <motion.circle
            cx="20"
            cy="3"
            r="2.2"
            fill="currentColor"
            stroke="none"
            animate={
              animate
                ? { opacity: [1, 0.3, 1], scale: [1, 1.25, 1] }
                : undefined
            }
            transition={{ duration: 1.9, repeat: Infinity, ease: "easeInOut" }}
            style={{ transformOrigin: "20px 3px" }}
          />
          <rect x="6.5" y="9" width="27" height="22.5" rx="8" />
          <path d="M6.5 17H4m29.5 0H36" />

          {/* Eyes blink on a fifth period, and drift side to side so the face
              looks around rather than staring. */}
          <motion.g
            fill="currentColor"
            stroke="none"
            animate={animate ? { x: [0, 1.6, 0, -1.6, 0] } : undefined}
            transition={{ duration: 7.3, repeat: Infinity, ease: "easeInOut" }}
          >
            <motion.g
              animate={animate ? { scaleY: [1, 1, 0.1, 1] } : undefined}
              transition={{
                duration: 4.9,
                repeat: Infinity,
                times: [0, 0.9, 0.95, 1],
                ease: "easeInOut",
              }}
              style={{ transformOrigin: "20px 18px" }}
            >
              <circle cx="14.5" cy="18" r="2.7" />
              <circle cx="25.5" cy="18" r="2.7" />
            </motion.g>
          </motion.g>

          <path d="M15.5 24.5c2.6 2.2 6.4 2.2 9 0" />
        </g>
      </svg>
    </span>
  );
}

export function AssistantBeacon() {
  const reduced = useReducedMotion() ?? false;
  const chatOpen = useChatOpen();
  const [visible, setVisible] = useState(false);
  const [message, setMessage] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Focus comes back here after the panel closes. It cannot be restored by
  // the panel itself: this button unmounts while the panel is open, so the
  // node the panel remembered is detached by then and focusing it does
  // nothing — focus silently falls to <body>.
  useEffect(() => {
    if (!chatOpen && consumePendingFocus()) buttonRef.current?.focus();
  }, [chatOpen]);

  useEffect(() => {
    const show = setTimeout(() => {
      setVisible(true);
    }, APPEAR_AFTER_MS);
    const speak = setTimeout(() => {
      setMessage(true);
    }, MESSAGE_AFTER_MS);
    return () => {
      clearTimeout(show);
      clearTimeout(speak);
    };
  }, []);

  // The panel replaces the beacon rather than sitting behind it — two glowing
  // things in one corner is a collision, not a composition.
  if (chatOpen) return null;

  const slide = reduced
    ? { duration: 0 }
    : ({ type: "spring", stiffness: 170, damping: 20 } as const);

  return (
    // A full-viewport clipping layer, then the beacon inside it.
    //
    // The robot enters from `x: 110` — beyond the right edge, which is what
    // makes it read as sliding in from the side. A translated element still
    // contributes to the document's scrollable area, so without this the page
    // grew ~110px wider and showed a horizontal scrollbar for the length of
    // the entrance. `overflow-hidden` on a fixed, full-viewport layer clips
    // the arrival; `pointer-events-none` here with `auto` on the controls
    // means the layer never intercepts a click meant for the page.
    <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden">
      {/* The data attributes are hooks for the audit scripts, not styling. This
        arrives on a timer and animates in, and an element measured
        mid-entrance is at partial opacity — which axe correctly reports as a
        contrast failure even though the resting state is fine. */}
      <div
        data-assistant-root
        className="pointer-events-none absolute right-0 bottom-8 flex items-center gap-3 pr-5 sm:bottom-12 sm:pr-8"
      >
        <AnimatePresence>
          {visible && message && !dismissed && (
            <motion.div
              key="message"
              data-assistant-bubble
              initial={reduced ? false : { opacity: 0, x: 48, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={
                reduced ? { opacity: 0 } : { opacity: 0, x: 34, scale: 0.94 }
              }
              transition={slide}
              className="glass pointer-events-auto relative max-w-[15rem] rounded-2xl rounded-br-sm border border-accent-dim py-3.5 pr-9 pl-4 shadow-[0_22px_60px_-26px_var(--color-accent)] sm:max-w-[17rem]"
            >
              <p
                aria-live="polite"
                className="text-2xs leading-relaxed text-text"
              >
                {MESSAGE}
              </p>
              <button
                type="button"
                onClick={() => {
                  setDismissed(true);
                }}
                className="absolute top-2 right-2 rounded-full p-1.5 text-text-dim transition-colors duration-200 hover:text-text"
              >
                <span className="sr-only">Dismiss the assistant message</span>
                <svg
                  aria-hidden="true"
                  viewBox="0 0 12 12"
                  className="size-2.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                >
                  <path d="M2 2l8 8M10 2l-8 8" />
                </svg>
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {visible && (
            <motion.button
              key="robot"
              ref={buttonRef}
              type="button"
              onClick={() => {
                openChat(buttonRef.current);
              }}
              // Enters from beyond the right edge, so it reads as sliding in
              // from the side rather than materialising in the corner.
              initial={reduced ? false : { opacity: 0, x: 110, rotate: 12 }}
              animate={{ opacity: 1, x: 0, rotate: 0 }}
              exit={reduced ? { opacity: 0 } : { opacity: 0, x: 80 }}
              transition={slide}
              whileHover={reduced ? undefined : { scale: 1.12, rotate: -5 }}
              whileTap={reduced ? undefined : { scale: 0.93 }}
              className="pointer-events-auto size-16 shrink-0 rounded-2xl sm:size-[4.5rem]"
            >
              <span className="sr-only">
                Talk to the assistant about Iftikhar&rsquo;s work
              </span>
              {/* The idle bob lives on an inner element, not on the button.
                A hit target that never stops moving is a hit target you have
                to chase — and it is genuinely un-clickable to anything that
                waits for the element to settle first, which is both automated
                tooling and anyone using switch or eye-tracking input. The
                drawing floats; the button stays exactly where it was put. */}
              <motion.span
                className="block size-full"
                animate={reduced ? undefined : { y: [0, -5, 0] }}
                transition={{
                  duration: 3.1,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <RobotFace animate={!reduced} />
              </motion.span>
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
